import { Injectable } from '@angular/core';
import {
  addSeconds,
  differenceInMilliseconds,
  differenceInSeconds,
  getMinutes,
  getSeconds,
  isAfter,
  isBefore,
  parseISO,
  startOfHour,
  startOfMinute,
  startOfSecond,
  subSeconds
} from 'date-fns';
import { BehaviorSubject, Observable, Subject, Subscription, of } from 'rxjs';
import { debounceTime, delay, map, takeUntil, tap } from 'rxjs/operators';
import { Track } from 'src/app/interfaces/api/track';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { TrackService } from 'src/app/services/api/tracks/track.service';

interface TrackData {
  createdDate: string;
  tracker: string;
  lat: number;
  lon: number;
  z: number;
}

interface TrackerData {
  tracker: string;
  startDate: string;
  endDate: string;
  lat: number;
  lon: number;
  z: number;
  missing?: boolean
}

@Injectable({
  providedIn: 'root'
})

export class PlayerService {
  tracksMap: Map<string, TrackData[]> = new Map(); // Map<`createdDate`, TrackData[]>
  lastTracksMap: Map<string, TrackerData[]> = new Map(); // Map<`tracker`, TrackerData[]>
  lastPositionOfTracksCaller$: BehaviorSubject<{ startDate: string, endDate: string, trackers: string[] }> = new BehaviorSubject({ startDate: "", endDate: "", trackers: [] as string[] });
  hasFullyFetched: boolean = false;
  startDate!: string;
  endDate!: string;
  hasFetchedLastPositionTillEnd: boolean = false;
  subscriptions: { subscription: Subscription, subject: Subject<void>, subjectId: number }[] = [];
  bufferValue$ = new BehaviorSubject<number>(0);
  tracksLimit: number = 10;
  trackersCount!: number;
  trackers: string[] = [];
  bufferPercentage: number = 0;
  public bufferProgressing: boolean = false;

  constructor(
    private trackService: TrackService
  ) {
  }

  initDates(startDate: string, endDate: string) {
    this.startDate = startDate;
    this.endDate = endDate;
  }

  setEndDate(endDate: string) {
    this.endDate = endDate;
  }

  /**
   * @param start: string :>> Date string where we started looking for last tracks.
   * @param end: string :>> Date string where we finished looking for last tracks
   * @param trackers: string[] :>> ids of trackers that were requested to the endpoint
   * @param tracks: TrackData[] :>> last tracks that were sent back from the endpoint
   */
  storeLastTracksData(start: string, end: string, trackers: string[], tracks: TrackData[] = []) {
    if (start == end) return;
    let noDataTrackers = [...trackers];
    for (let track of tracks) {
      if (!this.lastTracksMap.has(track.tracker || track.tracker)) {
        this.lastTracksMap.set(track.tracker, []);
      }

      let lastTracks = this.lastTracksMap.get(track.tracker);
      let prevTrack = lastTracks?.find(t => this.isAfter(t.startDate, track.createdDate) && this.isBefore(t.endDate, track.createdDate)
        && t.lat == track.lat && t.lon == track.lon && t.z == track.z);
      if (prevTrack) {
        prevTrack.startDate = isBefore(prevTrack.startDate, track.createdDate) ? prevTrack.startDate : track.createdDate;
        prevTrack.endDate = isAfter(prevTrack.endDate, track.createdDate) ? prevTrack.endDate : track.createdDate;
      }

      if (noDataTrackers.includes(track.tracker))
        noDataTrackers.splice(noDataTrackers.indexOf(track.tracker), 1);
    }
    for (let tracker of noDataTrackers) {
      this.updateLastTrackData(start, end, tracker);
    }
  }

  /**
   * TracksMap cache: we store here tracksData
   * @param start 
   * @param end 
   * @param tracks : Record<string, TrackData[] :>> Record<`trackerId`, TrackData[]>
   */
  storeTracksData(start: string, end: string, trackers: string[], tracksByTracker: Record<string, TrackData[]> = {}) {
    let missingTrackers = [...trackers];
    let availableTrackers: string[] = [];
    let storedTracks: TrackData[] = [];
    Object.keys(tracksByTracker).forEach(tracker => {
      let tracks = tracksByTracker[tracker];
      if (!tracks) return;
      storedTracks = storedTracks.concat(tracks);
      for (let track of tracks) {
        if (!this.tracksMap.has(track.createdDate))
          this.tracksMap.set(track.createdDate, []);

        let tracksData = this.tracksMap.get(track.createdDate);
        if (!tracksData?.find(t => t.tracker == track.tracker))
          tracksData?.push(track);
      }
      if (!availableTrackers.includes(tracker))
        availableTrackers.push(tracker);
      if (missingTrackers.includes(tracker))
        missingTrackers.splice(missingTrackers.indexOf(tracker), 1);
    });
    let startDate = new Date(start).getTime();
    let endDate = new Date(end).getTime();
    for (let time = startDate; time <= endDate; time += 1000) {
      let { missingTrackers } = this.checkTracksMap(time, availableTrackers);
      if (missingTrackers.length) {
        this.storeLastTracksData(start, new Date(time).toISOString(), missingTrackers);//, storedTracks, false);
      }
    }

    if (missingTrackers.length) {
      this.storeLastTracksData(start, end, missingTrackers, storedTracks);
    }
  }

  getPrevLastTrack(start: string, end: string, tracker: string): TrackerData | undefined {
    let trackerData = this.lastTracksMap.get(tracker);
    if (!trackerData) return undefined;
    return trackerData.find(td => this.isAfter(td.endDate, start) && this.isBefore(td.endDate, end));
  }

  updateLastTrackData(start: string, end: string, tracker: string) {
    let prevTrack: TrackData | undefined;
    let trackerData = this.lastTracksMap.get(tracker);
    if (!trackerData) {
      prevTrack = this.getLastFromTracksMap(this.startDate, end, tracker);
      if (prevTrack) {
        trackerData = [{ tracker: prevTrack.tracker, startDate: prevTrack.createdDate, endDate: end, lat: prevTrack.lat, lon: prevTrack.lon, z: prevTrack.z }];
        this.lastTracksMap.set(tracker, trackerData);
      } else {
        this.lastTracksMap.set(tracker, [{ tracker, startDate: start, endDate: end, lat: 0, lon: 0, z: 0, missing: true }])
      }
      return;
    }

    prevTrack = this.getLastFromTracksMap(start, end, tracker);

    if (prevTrack && differenceInSeconds(end, prevTrack.createdDate) == 0) {
      return;
    }
    if (prevTrack) {
      let prevTrackerData = this.getPrevLastTrack(prevTrack.createdDate, end, tracker);
      let { lat, lon, z } = prevTrackerData || {};
      if (prevTrackerData && prevTrack.lat == lat && prevTrack.lon == lon && prevTrack.z == z) {
        prevTrackerData.endDate = end;
      } else
        trackerData!.push({
          tracker,
          startDate: prevTrack.createdDate,
          endDate: end,
          lat: prevTrack.lat,
          lon: prevTrack.lon,
          z: prevTrack.z,
          missing: false
        });
      return;
    }
    let prevTrackerData = this.getPrevLastTrack(start, end, tracker);
    let { lat, lon, z } = prevTrackerData || {};
    if (prevTrackerData) {
      prevTrackerData.endDate = end;
    } else {
      prevTrackerData = this.getLastTrackerData(this.startDate, end, tracker);
      if (prevTrackerData)
        prevTrackerData.endDate = end;
      else {
        trackerData.push({
          tracker,
          startDate: start,
          endDate: end,
          lat: 0,
          lon: 0,
          z: 0,
          missing: true
        });
      }

    }
  }


  /**
   * 
   * @param createdDate: string :>> used to find the relevant tracksMap to look into + find tracks that were created last before createdDate (or at the same time)
   * @returns { tracks: TrackData[], noDataTrackers: string[] }
   *    - tracks :>> matched tracks that were found in tracksMap cache for the most relevant date string
   *    - noDataTrackers :>> trackers that are known to have no tracks for the matching period in the database
   */
  getTracksData(createdDate: string): TrackData[] {
    let tracksData = this.tracksMap.get(createdDate);
    if (!tracksData) return [];
    return [...tracksData.values()];
  }


  /**
   * Finds and/or generates tracks from both tracksMap and lastTracksMap cache. 
   * @param createdDate 
   * @param trackers 
   * @returns { tracks: TrackData[], hasAllTracks: boolean, noDataTrackers: string[], missingTrackers: string[]  }
   */
  getTracksFromCache(createdDate: string, trackers: string[]) {
    let tracksData = this.getTracksData(createdDate);
    let missingTrackers = [...trackers];

    for (let track of tracksData) {
      const idx = missingTrackers.indexOf(track.tracker);
      if (idx !== -1) missingTrackers.splice(idx, 1);
    }

    const { tracks: lastTracks, noDataTrackers, missingTrackers: stillMissing } = this.generateTracks(createdDate, missingTrackers);

    const allTracks = [...tracksData, ...lastTracks];

    return {
      tracks: allTracks,
      missingTrackers: stillMissing,
      noDataTrackers
    };
  }

  getLastFromTracksMap(start: string, end: string, trackerId: string): TrackData | undefined {

    let lastTrack: TrackData | undefined;
    let lastDate = undefined;
    let startDate = new Date(start).getTime();
    let endDate = new Date(end).getTime();
    for (let [createdDate, tracks] of this.tracksMap) {
      const date = new Date(createdDate).getTime();
      if (date < startDate || date > endDate) continue;

      for (let track of tracks) {
        if (track.tracker != trackerId) continue;
        if (!lastDate || new Date(track.createdDate).getTime() > lastDate) {
          lastTrack = track;
          lastDate = new Date(lastTrack.createdDate).getTime();
        }
      }
    }
    return lastTrack;
  }

  getLastTrackerData(start: string, end: string, trackerId: string): TrackerData | undefined {
    let lastTrack = this.getLastFromTracksMap(start, end, trackerId);
    if (!lastTrack) {
      let trackerData = this.getPrevLastTrack(start, end, trackerId);
      return trackerData;
    }
    return {
      tracker: lastTrack.tracker,
      lat: lastTrack.lat,
      lon: lastTrack.lon,
      z: lastTrack.z,
      startDate: lastTrack.createdDate,
      endDate: end
    }
  }

  getUncachedDates(createdDate: string, trackers: string[] = []) {
    let { startDate, endDate } = this.getNormalizedIntervalDates(createdDate);
    let nextCreatedDate = createdDate;
    if (!trackers.length || !this.hasCache(createdDate, trackers)) {
      return { startDate, endDate, createdDate };
    }
    if (isAfter(parseISO(endDate), parseISO(this.endDate))) return {};
    do {
      nextCreatedDate = addSeconds(parseISO(endDate), 1).toISOString();
      const { startDate: nextStartDate, endDate: nextEndDate } = this.getNormalizedIntervalDates(nextCreatedDate);
      startDate = nextStartDate;
      endDate = nextEndDate;
      if (this.isAfter(parseISO(nextCreatedDate), parseISO(this.endDate))) break;
    } while (this.hasCache(nextCreatedDate, trackers));

    if (this.hasCache(nextCreatedDate, trackers)) return {};
    if (this.isAfter(parseISO(startDate), parseISO(this.endDate))) return {};
    if (isAfter(parseISO(endDate), parseISO(this.endDate))) endDate = this.endDate;
    return { startDate, endDate, createdDate: nextCreatedDate };
  }

  fetchNextTracks(createdDate: string, trackers: string[] = []) {
    let { startDate, endDate = "", createdDate: nextCreatedDate } = this.getUncachedDates(createdDate, trackers);
    if (!startDate || !nextCreatedDate) {
      return this.cancelAllRequests();
    }

    const { missingTrackers, tracks } = this.getTracksFromCache(nextCreatedDate, trackers);
    let cacheTracks: Record<string, TrackData[]> = {};
    for (let track of tracks) {
      cacheTracks[track.tracker] = [track]
    }

    const cancelRequest$ = new Subject<void>();
    const subjectId = Math.floor(Math.random() * 100000)
    this.bufferProgressing = true;
    if (!missingTrackers.length) {
      return this.cancelAllRequests();
    }

    const subscription = this.trackService.getPositionAtTime({ startDate, endDate, trackers: missingTrackers, limit: this.tracksLimit })
      .pipe(
        takeUntil(
          cancelRequest$
        ),
      )
      .pipe(
        debounceTime(1000)
      )
      .pipe(
        delay(1000)
      )
      .subscribe((apiTracks: any) => {
        if (startDate && endDate) {
          Object.keys(apiTracks).forEach(tracker => {
            let tracks = apiTracks[tracker];
            tracks.sort((a: Track, b: Track) => isBefore(a.createdDate, b.createdDate) ? -1 : 1);
            tracks.forEach((track: Track)  => { 
              track.tracker = (track.tracker as Tracker).id || track.tracker;
              track.createdDate = startOfSecond(track.createdDate).toISOString(); 
            });
          });
          this.storeTracksData(startDate, endDate, missingTrackers, { ...apiTracks });
          this.cancelAllRequests();
        }
        if (!cancelRequest$.isStopped && this.isBefore(parseISO(nextCreatedDate), parseISO(this.endDate)))
          this.fetchNextTracks(nextCreatedDate, trackers);
        else
          this.bufferProgressing = false;
      });
    this.subscriptions.push({ subscription, subject: cancelRequest$, subjectId });
  }

  public cancelAllRequests() {
    if (!this.subscriptions.length) return;
    this.subscriptions.forEach((subObj: { subscription: Subscription, subject: Subject<void> }) => {
      const { subscription, subject } = subObj;
      subscription.unsubscribe();
      subject.next();
      subject.complete();
    });
    this.subscriptions = [];
    this.bufferProgressing = false;
  }

  isBufferProgressing(): boolean {
    return this.bufferProgressing;
  }

  getBufferValue(): Observable<number> {
    return this.bufferValue$.asObservable();
  }

  public calculateBufferPercentage(createdDate: string, trackers: string[], bufferEndTime: string | undefined = undefined): number {
    const startDate = parseISO(createdDate);

    if (!bufferEndTime) {
      let { startDate, endDate } = this.getUncachedDates(createdDate, trackers);
      bufferEndTime = startDate;
    }

    if (!bufferEndTime) return 0;

    let bufferValue = differenceInSeconds(bufferEndTime, this.startDate);
    this.bufferValue$.next(bufferValue);
    const bufferEndDate = parseISO(bufferEndTime);
    // const finalEndDate = parseISO(this.endDate);
    const finalEndDate = new Date(this.endDate).toISOString();
    const durationBetweenStartAndBufferEnd = differenceInMilliseconds(bufferEndDate, startDate);
    const totalDuration = differenceInMilliseconds(finalEndDate, startDate);
    let percentage = Math.round((durationBetweenStartAndBufferEnd) / (totalDuration || 1) * 100 * 100) / 100;

    this.bufferPercentage = percentage > 100 ? 100 : percentage;
    return bufferValue;
  }

  hasCache(createdDate: string, trackers: string[]): boolean {
    if (!this.tracksMap.has(createdDate) && this.lastTracksMap.size == 0) return false;
    let { hasAllTracks, missingTrackers } = this.checkTracksMap(createdDate, trackers);

    if (hasAllTracks) return true;
    if (!missingTrackers.length) {
      console.error('--> Logical error in checkTracksMap');
      return true;
    }
    let { missingTrackers: stillMissing, noDataTrackers, matchedTrackers } = this.checkLastTracksMap(createdDate, missingTrackers);

    if (!stillMissing.length)
      return true;

    return false;

  }

  getNormalizedIntervalDates(time: string, trackers: string[] = []): { startDate: string, endDate: string, time: string } {
    let startDate, endDate;

    if (!this.trackersCount && trackers.length)
      this.trackersCount = trackers.length;

    let trackersCount = this.trackersCount ?? trackers.length;

    if (trackersCount >= 50) { // Intervals in seconds (if 50 trackers or more)
      let dizaines = 3;

      if (trackersCount >= 85) dizaines = 1;
      else if (trackersCount >= 65) dizaines = 2;

      let seconds = getSeconds(parseISO(time));
      seconds = seconds - (seconds % (10 * dizaines));
      startDate = addSeconds(startOfMinute(parseISO(time)), seconds).toISOString();
      endDate = addSeconds(parseISO(startDate), 10 * dizaines).toISOString();
    } else if (trackersCount >= 17) { // 60 seconds intervals
      // let seconds = getSeconds(parseISO(time));
      // seconds = seconds - (seconds % (10 * dizaines));
      startDate = startOfMinute(parseISO(time)).toISOString();
      endDate = addSeconds(parseISO(startDate), 60).toISOString();
    } else if (trackersCount >= 7) { // 1m30 intervals
      const currentTime = parseISO(time);
      const currentMinute = getMinutes(currentTime);
      const currentSecond = getSeconds(currentTime);

      // Calculating the start of the closest 1m30 interval
      let secondsToAdd = Math.floor((currentMinute * 60 + currentSecond) / 90) * 90;
      startDate = addSeconds(startOfHour(parseISO(time)), secondsToAdd).toISOString();
      endDate = addSeconds(parseISO(startDate), 90).toISOString();
    } else { // 2min intervals
      const currentTime = parseISO(time);
      const currentMinute = getMinutes(currentTime);
      const currentSecond = getSeconds(currentTime);

      // Calculating the start of the closest 2-minute interval
      let secondsToAdd = Math.floor((currentMinute * 60 + currentSecond) / 120) * 120;
      startDate = addSeconds(startOfHour(parseISO(time)), secondsToAdd).toISOString();
      endDate = addSeconds(parseISO(startDate), 120).toISOString();
    }

    return { startDate, endDate, time: startOfSecond(time).toISOString() };
  }

  getPositionOfTracksAtTime(query: { createdDate: string, trackers: string[], speed: number }): Observable<{ tracks: TrackData[], speed?: number, seconds?: number }[]> {
    let { createdDate, trackers, speed } = query;
    createdDate = startOfSecond(createdDate).toISOString();

    if (!this.trackersCount && trackers.length)
      this.trackersCount = trackers.length;

    this.updateTracksLimit(speed);
    if (!trackers.length) {
      return of([]);
    }

    this.calculateBufferPercentage(createdDate, trackers);
    let { startDate, endDate } = this.getNormalizedIntervalDates(createdDate, trackers);
    let initialCreatedDate = speed > 1 ? subSeconds(parseISO(createdDate), speed).toISOString() : null;

    let noCachePeriod = this.getUncachedDates(initialCreatedDate ?? createdDate, trackers)?.startDate;
    if (!this.bufferProgressing && this.bufferPercentage < 100 && noCachePeriod)
      this.fetchNextTracks(initialCreatedDate ?? createdDate, trackers);

    if (speed > 1 && initialCreatedDate && this.hasCache(initialCreatedDate, trackers)) {
      let cachedData = this.getFastForwardedTracks(createdDate, speed, trackers);
      if (cachedData.length) {
        return of(cachedData);
      }
    }

    let { tracks, missingTrackers } = this.getTracksFromCache(createdDate, trackers);

    if (!missingTrackers.length) {
      return of([{ tracks }]);
    }

    startDate = subSeconds(parseISO(createdDate), 5).toISOString();
    return this.trackService.getLastPositions({ startDate, endDate: createdDate, trackers: missingTrackers }).pipe(
      tap((tracks: Track[]) => {
        tracks.forEach(track => { 
          track.tracker = (track.tracker as Tracker).id || track.tracker;
          track.createdDate = startOfSecond(track.createdDate).toISOString(); 
        });
        this.storeLastTracksData(startDate, createdDate, trackers, tracks as TrackData[]);
      }),
      map(tracks => [{ tracks: [...this.getTracksFromCache(createdDate, trackers).tracks] }])
    )
  }

  getLastPositionOfTracks(payload: { startDate: string, endDate: string, trackers: string[], speed: number }): Observable<any> {
    if (this.bufferProgressing)
      this.cancelAllRequests();
    let { startDate, endDate, trackers, speed } = payload;
    if (!this.hasFullyFetched) {
      this.trackService.getLastPositions({ startDate: this.startDate, endDate: this.endDate, trackers }).subscribe((tracks: Track[]) => {
        tracks.forEach(track => { 
          track.tracker = (track.tracker as Tracker).id || track.tracker;
          track.createdDate = startOfSecond(track.createdDate).toISOString(); 
        });
        this.storeLastTracksData(this.startDate, this.endDate, trackers, tracks as TrackData[]);
        this.hasFullyFetched = true;
      });
    }

    if (isAfter(parseISO(endDate), parseISO(this.endDate))) return of([]);

    if (!this.trackersCount && trackers.length)
      this.trackersCount = trackers.length;

    this.calculateBufferPercentage(endDate, trackers);
    let { tracks, missingTrackers } = this.getTracksFromCache(endDate, trackers);
    let initialCreatedDate = speed > 1 ? subSeconds(parseISO(endDate), speed).toISOString() : null;

    if (initialCreatedDate && isBefore(parseISO(initialCreatedDate), parseISO(this.startDate)))
      initialCreatedDate = this.startDate;

    if (this.getUncachedDates(initialCreatedDate ?? endDate, trackers).startDate && this.bufferProgressing)
      this.fetchNextTracks(initialCreatedDate ?? endDate, trackers);

    if (!missingTrackers.length) {
      return of(tracks);
    }

    return this.trackService.getLastPositions({ ...payload, trackers: missingTrackers }).pipe(
      tap((tracks: Track[]) => {
        tracks.forEach(track => { 
          track.tracker = (track.tracker as Tracker).id || track.tracker;
          track.createdDate = startOfSecond(track.createdDate).toISOString(); 
        });
        this.storeLastTracksData(startDate, endDate, trackers, tracks as TrackData[]);//, true);
      })
    )
  }

  updateTracksLimit(speed: number) {
    // if (speed <= 1) this.tracksLimit = 20;
    // else if (speed <= 2) this.tracksLimit = 40;
    // else if (speed <= 4) this.tracksLimit = 80;
    // else if (speed <= 8) this.tracksLimit = 120;

    this.tracksLimit = 120;
  }

  getFastForwardedTracks(createdDate: string, speed: number, trackers: string[]) {
    let cachedData: { tracks: TrackData[], speed?: number, index?: number }[] = [];
    let nextCreatedDate = "";
    let step = speed > 25 ? speed / 25 : 1;
    let index = 0;
    for (let i = 0; i < speed; i += step) {
      let seconds = Math.floor(i);
      nextCreatedDate = addSeconds(parseISO(createdDate), seconds).toISOString();
      let { tracks, missingTrackers } = this.getTracksFromCache(nextCreatedDate, trackers);
      cachedData.push({ tracks: tracks, speed: speed > 25 ? 25 : speed, index });
      index++;
    }
    return cachedData;
  }

  /**
   * Generating TrackData[] from LastTracksMap
   * @param createdDate: string
   * @param trackerIds: string[]
   * @returns {tracks: TrackData[], noDataTrackers: string[], missingTrackers: string[] }
   */
  generateTracks(createdDate: string, trackerIds: string[]): {
    tracks: TrackData[],
    noDataTrackers: string[],
    missingTrackers: string[]
  } {
    let tracks: TrackData[] = [];
    let noDataTrackers: string[] = [];
    let missingTrackers: string[] = [];

    for (let tracker of trackerIds) {
      const lastDataArr = this.lastTracksMap.get(tracker);

      if (!lastDataArr || lastDataArr.length === 0) {
        missingTrackers.push(tracker);
        continue;
      }

      const match = lastDataArr.find(ld =>
        this.isBefore(ld.startDate, createdDate) && this.isBefore(createdDate, ld.endDate)
      );

      if (!match) {
        missingTrackers.push(tracker);
        continue;
      }

      if (match.missing) {
        noDataTrackers.push(tracker);
        continue;
      }

      tracks.push({
        tracker,
        createdDate,
        lat: match.lat,
        lon: match.lon,
        z: match.z
      });
    }

    return { tracks, noDataTrackers, missingTrackers };
  }

  /**
   * 
   * @param createdDate 
   * @param trackers 
   * @returns { hasAllTracks: boolean, noDataTrackers: string[], missingTrackers: string[] }
   *    - hasAllTracks: >> if this is true, it means all requested trackers have tracks in cache of tracksMap
   *    - missingTrackers :>> trackers without tracks in cache BUT we don't know if they have tracks in the database 
   */
  checkTracksMap(createdDate: string | number, trackers: string[]) {
    createdDate = typeof createdDate == "number" ? new Date(createdDate).toISOString() : createdDate;
    const tracks = this.getTracksData(createdDate);
    let missingTrackers = [...trackers];

    for (let t of tracks) {
      const idx = missingTrackers.indexOf(t.tracker);
      if (idx !== -1) missingTrackers.splice(idx, 1);
    }

    return {
      hasAllTracks: missingTrackers.length === 0,
      missingTrackers
    };
  }

  /**
   * 
   * @param startDate 
   * @param endDate 
   * @param trackers: string[] :>> requested trackers
   * @returns { hasAllTrackers: boolean, missingTrackers: string[], noDataTrackers: string[] } 
   *    - hasAllTrackers :>> if this is true, it means all requested trackers have tracks in cache of lastTracksMap
   *    - noDataTrackers :>> trackers that are known to have no tracks for the matching period in the database
   *    - missingTrackers :>> trackers without tracks in cache OR trackers were last updated AFTER endDate
   */
  checkLastTracksMap(createdDate: string, trackers: string[] = []) {
    let missingTrackers: string[] = [];
    let noDataTrackers: string[] = [];
    let matchedTrackers: string[] = [];

    for (let tracker of trackers) {
      const arr = this.lastTracksMap.get(tracker);

      if (!arr || arr.length === 0) {
        missingTrackers.push(tracker);
        continue;
      }

      const match = arr.find(ld => this.isAfter(ld.endDate, createdDate) && this.isBefore(ld.startDate, createdDate));
      if (!match) {
        missingTrackers.push(tracker);
        continue;
      }

      if (match.missing) {
        noDataTrackers.push(tracker);
        continue;
      }

      matchedTrackers.push(tracker);
    }

    return { missingTrackers, noDataTrackers, matchedTrackers };
  }

  /**
   * Is before or equal
   * @param start 
   * @param end 
   * @returns true if the first date before the second one or EQUAL

   */
  isBefore(start: string | Date, end: string | Date) {
    return !isAfter(start, end);
  }

  /**
   * Is after or equal
   * @param start 
   * @param end 
   * @returns true if the first date after the second one or EQUAL
   */
  isAfter(start: string | Date, end: string | Date) {
    return !isBefore(start, end);
  }

}
